import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, FolderKanban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", collaborator_id: "", group_id: "" });

  const load = async () => {
    const [p, c, g] = await Promise.all([api.get("/projects"), api.get("/collaborators"), api.get("/groups")]);
    setProjects(p.data); setCollabs(c.data); setGroups(g.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.collaborator_id) return toast.error("Naziv i saradnik su obavezni");
    try {
      await api.post("/projects", {
        ...form,
        name: form.name.trim(),
        group_id: form.group_id || null,
      });
      toast.success("Projekat kreiran");
      setForm({ name: "", description: "", collaborator_id: "", group_id: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const collabName = (id) => collabs.find((c) => c.id === id)?.name;
  const groupName = (id) => groups.find((g) => g.id === id)?.name;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Rad u toku</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight">Projekti</h1>
          <p className="text-ink-muted mt-2 max-w-xl">
            Projekat grupiše više taskova između tebe i saradnika (npr. Snimanje videa).
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="new-project-btn">
          <Plus className="w-4 h-4" /> Novi projekat
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((p) => (
          <Link key={p.id} to={`/projekti/${p.id}`} data-testid={`project-card-${p.id}`} className="surface-card p-5 hover:bg-surface-secondary transition-colors">
            <div className="flex items-center gap-2 text-forest">
              <FolderKanban className="w-4 h-4" />
              <div className="font-heading font-semibold">{p.name}</div>
            </div>
            {p.description ? <div className="text-sm text-ink-muted mt-2 line-clamp-2">{p.description}</div> : null}
            <div className="mt-4 flex flex-wrap gap-1.5 text-xs">
              {p.group_id && <span className="px-2 py-0.5 rounded bg-surface-secondary border border-line">{groupName(p.group_id)}</span>}
              <span className="px-2 py-0.5 rounded bg-surface-secondary border border-line">{collabName(p.collaborator_id) || "—"}</span>
            </div>
          </Link>
        ))}
        {projects.length === 0 && <div className="col-span-full surface-card p-8 text-center text-ink-muted">Još nema projekata.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="project-dialog" className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Novi projekat</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Naziv</Label><Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Opis</Label><Textarea data-testid="project-desc-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label>Saradnik</Label>
              <Select value={form.collaborator_id} onValueChange={(v) => setForm({ ...form, collaborator_id: v })}>
                <SelectTrigger data-testid="project-collab-select" className="bg-white"><SelectValue placeholder="Izaberi" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {collabs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grupacija (opcionalno)</Label>
              <Select value={form.group_id || "none"} onValueChange={(v) => setForm({ ...form, group_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="project-group-select" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Bez grupacije</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Otkaži</button>
            <button className="btn-primary" onClick={submit} data-testid="project-save-btn">Sačuvaj</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
